import type {
  HTMLChakraProps,
  SystemProps,
  SystemStyleObject,
  ThemingProps
} from '@chakra-ui/system'
import {
  chakra,
  forwardRef,
  layoutPropNames,
  omitThemingProps,
  useMultiStyleConfig
} from '@chakra-ui/system'
import { mode } from '@chakra-ui/theme-tools'
import { __DEV__, callAll, split } from '@chakra-ui/utils'

import type { BoxProps, UseRadioProps } from '@chakra-ui/react'
import { Box, useRadio } from '@chakra-ui/react'

import {
  Buildings,
  Car,
  Craft,
  Czech,
  Europe,
  Gastro,
  House,
  Household,
  HouseholdProperty,
  Hut,
  Interruptions,
  Motobike,
  Other,
  Production,
  Property,
  Responsibility,
  Retail,
  Services,
  Stock,
  Truck,
  Vehicle,
  World,
  WorldCanUsa,
} from '../icons'
import { useRadioIconGroupContext } from './RadioIconGroup'

export const RadioIconIcons = {
  Car: 'car',
  Truck: 'truck',
  Motobike: 'motobike',
  Other: 'other',
  HouseholdProperty: 'householdProperty',
  House: 'house',
}
export type RadioIconIconsEnum = typeof RadioIconIcons[keyof typeof RadioIconIcons]

export interface IGetRadioIcon {
  icon: string
  colorScheme?: string
  defaultIconSize?: string
}

export const getRadioIcon = ({ icon, colorScheme, defaultIconSize = '28px' }: IGetRadioIcon) => {
  const defaultIconProps = {
    color: `${colorScheme ?? 'secondary'}.500`,
    width: defaultIconSize,
    height: defaultIconSize,
  }

  switch (icon) {
    case 'car':
      return <Car {...defaultIconProps} />
    case 'truck':
      return <Truck {...defaultIconProps} />
    case 'motobike':
      return <Motobike {...defaultIconProps} />
    case 'other':
      return <Other {...defaultIconProps} />
    case 'householdProperty':
      return <HouseholdProperty {...defaultIconProps} width="70px" />
    case 'house':
      return <House {...defaultIconProps} />
    case 'household':
      return <Household {...defaultIconProps} />
    case 'property':
      return <Property {...defaultIconProps} />
    case 'hut':
      return <Hut {...defaultIconProps} />
    case 'buildings':
      return <Buildings {...defaultIconProps} />
    case 'stock':
      return <Stock {...defaultIconProps} />
    case 'vehicle':
      return <Vehicle {...defaultIconProps} />
    case 'responsibility':
      return <Responsibility {...defaultIconProps} />
    case 'interruptions':
      return <Interruptions {...defaultIconProps} />
    case 'retail':
      return <Retail {...defaultIconProps} />
    case 'craft':
      return <Craft {...defaultIconProps} />
    case 'gastro':
      return <Gastro {...defaultIconProps} />
    case 'services':
      return <Services {...defaultIconProps} />
    case 'production':
      return <Production {...defaultIconProps} />
    case 'cs':
      return <Czech {...defaultIconProps} />
    case 'europe':
      return <Europe {...defaultIconProps} />
    case 'world-no-us-can':
      return <WorldCanUsa {...defaultIconProps} />
    case 'world':
      return <World {...defaultIconProps} />
  }
}

type Omitted = 'onChange' | 'defaultChecked' | 'checked' | 'icon'
interface BaseControlProps extends Omit<HTMLChakraProps<'div'>, Omitted> { }

export interface RadioIconProps extends UseRadioProps, ThemingProps<'Radio'>, BaseControlProps {
  /**
   * The spacing between the checkbox and its label text
   * @default 0.5rem
   * @type SystemProps["marginLeft"]
   */
  spacing?: SystemProps['marginLeft']
  /**
   * If `true`, the radio will occupy the full width of its parent container
   *
   * @deprecated
   * This component defaults to 100% width,
   * please use the props `maxWidth` or `width` to configure
   */
  isFullWidth?: boolean
  /**
   * Additional props to be forwarded to the `input` element
   */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
  icon?: string
  hideControl?: boolean
  description?: React.ReactNode | string
  iconSize?: string
  labelsx?: BoxProps['sx']
  inputlabelwrappersx?: BoxProps['sx']
}

/**
 * Radio component is used in forms when a user needs to select a single value from
 * several options.
 *
 * @see Docs https://chakra-ui.com/radio
 */
export const RadioIcon = forwardRef<RadioIconProps, 'input'>((props, ref) => {
  const group = useRadioIconGroupContext()
  const {
    onChange: onChangeProp,
    value: valueProp,
    icon,
    hideControl = false,
    description,
    variant,
    colorScheme,
  } = props

  const styles = useMultiStyleConfig('Radio', { ...props })

  const ownProps = omitThemingProps(props)

  const {
    children,
    isDisabled = group?.isDisabled,
    isFocusable = group?.isFocusable,
    inputProps: htmlInputProps,
    iconSize,
    labelsx = {},
    inputlabelwrappersx = {},
    ...rest
  } = ownProps

  let isChecked = props.isChecked
  if (group?.value != null && valueProp != null) {
    isChecked = group.value == valueProp // musi byt pouze 2x ==, protoze dochazi k problemum napr mezi int a string 
  }

  let onChange = onChangeProp
  if (group?.onChange && valueProp != null) {
    onChange = callAll(group.onChange, onChangeProp)
  }

  const name = props?.name

  const { getInputProps, getCheckboxProps, getLabelProps, getRootProps, htmlProps } = useRadio({
    ...rest,
    isChecked,
    isFocusable,
    isDisabled,
    onChange,
    name,
  })

  const [layoutProps, otherProps] = split(htmlProps, layoutPropNames as any)

  const checkboxProps = getCheckboxProps(otherProps)
  const inputProps = getInputProps(htmlInputProps, ref)
  const labelProps = getLabelProps()
  const rootProps = Object.assign({}, layoutProps, getRootProps())

  const rootStyles = {
    ...styles.container,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    verticalAlign: 'top',
    cursor: 'pointer',
    width: {
      base: '100%',
      sm: 'auto',
    },
    bg: isChecked
      ? mode('white', 'whiteAlpha.800')
      : mode(variant === 'simple' ? 'transparent' : 'gray.50', 'whiteAlpha.800'),
    borderColor: isChecked ? 'green' : mode('gray.400', 'whiteAlpha.800'),
  }

  const checkboxStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    ...styles.control,
  }

  const labelStyles: SystemStyleObject = {
    ...styles.label,
    userSelect: 'none',
    // marginStart: hideControl || variant === 'simple' ? 0 : spacing,
    fontWeight: description ? '500' : '400',
  }

  return (
    <chakra.label className="chakra-radio" {...rootProps} __css={rootStyles}>
      <Box
        sx={{
          display: { base: 'grid', sm: 'grid' },
          gap: 2,
          alignContent: 'center',
          alignItems: 'center',
          justifyContent: 'center',
          justifyItems: 'center',
        }}
      >
        {icon && getRadioIcon({ colorScheme, icon, defaultIconSize: iconSize })}

        <Box
          sx={{
            display: description !== undefined ? 'grid' : 'flex',
            gap: 2,
            placeContent: 'center',
            placeItems: 'center',
            ...inputlabelwrappersx,
          }}
        >
          <input className="chakra-radio__input" {...inputProps} />

          {!hideControl && (
            <chakra.span
              className="chakra-radio__control"
              {...checkboxProps}
              __css={{
                ...checkboxStyles,
                borderColor: 'primary.500',
                _checked: {
                  borderColor: 'primary.500',
                  _before: {
                    content: '""',
                    display: 'inline-block',
                    position: 'relative',
                    width: '2',
                    height: '2',
                    borderRadius: '50%',
                    background: 'primary.500',
                  },
                },
              }}
            />
          )}

          {children && (
            <chakra.span
              className="chakra-radio__label"
              {...labelProps}
              sx={labelsx}
              __css={labelStyles}
            >
              {children}
            </chakra.span>
          )}
        </Box>

        {description && description}
      </Box>
    </chakra.label>
  )
})

if (__DEV__) {
  RadioIcon.displayName = 'RadioIcon'
}
